/**
 * Live editing for text on a path.
 *
 * A plain textarea over the box would show the text as a flat block while typing
 * and only snap it onto the curve on blur — you'd be editing something that
 * doesn't look like what you're making. So the run keeps rendering on the curve
 * while a TRANSPARENT textarea sits on top and does nothing but collect
 * keystrokes: every change goes straight into the store, the element re-renders,
 * and the glyphs reflow along the path on the same frame.
 *
 * The textarea's own caret and selection are invisible for the same reason its
 * text is — both are laid out flat, i.e. nowhere near the letters — so the SVG run
 * draws the caret and the selection bands itself, and the pointer never touches
 * the textarea: a MOUSE SURFACE over the run maps the pointer onto the curve and
 * drives the field's selection from there. Without it, clicking or dragging over
 * a letter selected whatever character happened to sit at that spot in the flat
 * block, which is not the character you are pointing at.
 */
import { useRef, useState } from 'react';
import { layoutTextOnPath, textPathContent } from '../utils/textPathLayout';
import { usePrintEditorStore } from '../state/usePrintEditorStore';
import TextOnPath from './TextOnPath';

/** Word around an offset, for double-click — same class of run a textarea picks. */
function wordRange(text, index) {
    const str = String(text ?? '');
    const isWord = (c) => c && /\S/.test(c);
    if (!str.length) return { start: 0, end: 0 };
    let start = Math.max(0, Math.min(str.length, index));
    let end = start;
    // A click past the last letter of a word should still take that word.
    if (!isWord(str[start]) && isWord(str[start - 1])) start -= 1;
    if (!isWord(str[start])) return { start, end: start };
    while (start > 0 && isWord(str[start - 1])) start -= 1;
    end = start;
    while (end < str.length && isWord(str[end])) end += 1;
    return { start, end };
}

export default function TextPathEditor({ element, onInput, onCommit, onSelect }) {
    const ref = useRef(null);
    const anchor = useRef(0);
    const [caret, setCaret] = useState(() => (element.content || '').length);
    const [range, setRange] = useState(null);
    const zoom = usePrintEditorStore((s) => s.zoom) || 1;

    const w = Math.max(1, Number(element.width) || 1);
    const h = Math.max(1, Number(element.height) || 1);
    const fontSize = Number(element.styles?.fontSize) || 16;
    // Glyphs on an arch stand well outside the box, and the pointer surface has to
    // cover them or those letters can't be clicked at all — the same bleed the
    // renderer pads its layer by.
    const bleed = Math.ceil(fontSize * 1.5);

    const syncCaret = () => {
        const node = ref.current;
        if (!node) return;
        const start = Math.min(node.selectionStart, node.selectionEnd);
        const end = Math.max(node.selectionStart, node.selectionEnd);
        // Collapsed selection → the caret; a range → its leading edge, which is
        // where typing will land.
        setCaret(start);
        setRange(end > start ? { start, end } : null);
        // A range is also what the panel colours, exactly as in a text block.
        onSelect?.({ start: node.selectionStart, end: node.selectionEnd });
    };

    const select = (start, end) => {
        const node = ref.current;
        if (!node) return;
        node.setSelectionRange(Math.min(start, end), Math.max(start, end));
        syncCaret();
    };

    /**
     * The content offset the pointer is over: the nearest glyph CELL (its advance
     * along the tangent, its height across it), then the near or far edge of that
     * cell depending on which side of its centre the pointer fell — the same rule
     * a flat text field uses, measured along the curve instead of along a line.
     */
    const offsetAt = (event) => {
        const laid = layoutTextOnPath(element, w, h);
        const glyphs = laid?.glyphs || [];
        const text = textPathContent(element);
        if (!glyphs.length) return 0;

        const rect = event.currentTarget.getBoundingClientRect();
        // The surface is symmetric around the element box, so its centre is the
        // box centre whatever the element's rotation does to the bounding rect.
        let px = (event.clientX - (rect.left + rect.width / 2)) / zoom;
        let py = (event.clientY - (rect.top + rect.height / 2)) / zoom;
        const rot = ((Number(element.rotation) || 0) * Math.PI) / 180;
        if (rot) {
            const c = Math.cos(-rot);
            const s = Math.sin(-rot);
            [px, py] = [px * c - py * s, px * s + py * c];
        }
        const lx = px + w / 2;
        const ly = py + h / 2;

        let best = 0;
        let bestDist = Infinity;
        let after = false;
        glyphs.forEach((g, i) => {
            const rad = (g.angle * Math.PI) / 180;
            const tx = Math.cos(rad);
            const ty = Math.sin(rad);
            const dx = lx - g.x;
            const dy = ly - g.y;
            const along = dx * tx + dy * ty;
            // Baselines sit at the bottom of the ink, so the cell reaches up.
            const across = -dx * ty + dy * tx + fontSize * 0.32;
            const dist = Math.hypot(
                Math.max(0, Math.abs(along) - g.advance / 2),
                Math.max(0, Math.abs(across) - fontSize * 0.5),
            );
            if (dist < bestDist) {
                bestDist = dist;
                best = i;
                after = along > 0;
            }
        });
        const g = glyphs[best];
        if (!after) return g.index;
        const next = glyphs[best + 1];
        return next ? next.index : text.length;
    };

    return (
        <>
            {/* Not interactive while editing: a click on a glyph is handled by the
                surface below, not by the element's own drag/select handlers. */}
            <TextOnPath
                element={element}
                showGuide
                caret={caret}
                selection={range}
                interactive={false}
            />
            <textarea
                ref={ref}
                autoFocus
                defaultValue={element.content || ''}
                spellCheck={false}
                className="fig-path-input"
                // Transparent in every respect — the glyphs on the path are the only
                // visible text — and deaf to the pointer, which the surface below
                // handles so that clicks land on the letter they look like they hit.
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    margin: 0,
                    padding: 0,
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                    background: 'transparent',
                    color: 'transparent',
                    caretColor: 'transparent',
                    font: 'inherit',
                    fontSize,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                }}
                // autoFocus alone drops the caret at index 0, so typing would prepend
                // to existing text. Land it at the end instead.
                onFocus={(e) => {
                    const end = e.target.value.length;
                    e.target.setSelectionRange(end, end);
                    setCaret(end);
                    setRange(null);
                }}
                onChange={(e) => {
                    onInput?.(e.target.value);
                    syncCaret();
                }}
                onSelect={syncCaret}
                onKeyUp={syncCaret}
                onBlur={(e) => onCommit?.(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') e.currentTarget.blur();
                    // Canvas shortcuts (delete, arrows, ⌘G …) must not fire while typing.
                    e.stopPropagation();
                }}
            />
            {/* The mouse surface. preventDefault keeps focus on the (invisible)
                field, so the keyboard keeps working while the pointer drives the
                selection along the curve. */}
            <div
                role="presentation"
                style={{
                    position: 'absolute',
                    inset: -bleed,
                    cursor: 'text',
                }}
                onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const at = offsetAt(e);
                    ref.current?.focus({ preventScroll: true });
                    if (e.shiftKey) select(anchor.current, at);
                    else {
                        anchor.current = at;
                        select(at, at);
                    }
                    e.currentTarget.setPointerCapture(e.pointerId);
                }}
                onPointerMove={(e) => {
                    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
                    select(anchor.current, offsetAt(e));
                }}
                onPointerUp={(e) => {
                    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                        e.currentTarget.releasePointerCapture(e.pointerId);
                    }
                }}
                onDoubleClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const word = wordRange(ref.current?.value, offsetAt(e));
                    anchor.current = word.start;
                    select(word.start, word.end);
                }}
            />
        </>
    );
}
